/**
 * Manager Message Section Component
 * Handles homepage message management for managers
 */
import React from 'react';
import { MessageSquare, Save } from 'lucide-react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';

export function ManagerMessageSection({
    managerMessage,
    setManagerMessage,
    savingMessage,
    onSave,
    isResidential
}) {
    return (
        <div className="space-y-6" data-testid="manager-message">
            <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold">Homepage Message</h2>
            </div>

            <div className={`card-base p-4 ${isResidential ? 'bg-stone-50 border-stone-200' : 'bg-emerald-50 border-emerald-200'}`}>
                <p className={`text-sm ${isResidential ? 'text-stone-800' : 'text-emerald-800'}`}>
                    <strong>About this feature:</strong> This message will be displayed prominently on the homepage for all visitors. Use it to share important announcements, seasonal greetings, or special news.
                </p>
            </div>
            
            <div className="card-base space-y-4">
                <div>
                    <Label className="text-base font-medium">Your Message</Label>
                    <Textarea 
                        value={managerMessage.message} 
                        onChange={(e) => setManagerMessage(prev => ({ ...prev, message: e.target.value }))}
                        placeholder={isResidential 
                            ? "Welcome to our community! We're delighted to have you here today..."
                            : "Welcome to Burston Garden Centre! We're delighted to have you visit us today..."
                        }
                        rows={5}
                        className="mt-2"
                        data-testid="manager-message-input"
                    />
                    <p className="text-xs text-muted-foreground mt-2">
                        Leave blank to hide the message from the homepage.
                    </p>
                </div>

                {managerMessage.updated_at && (
                    <div className="text-sm text-muted-foreground border-t pt-3">
                        Last updated: {new Date(managerMessage.updated_at).toLocaleString('en-GB', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                        })}
                        {managerMessage.updated_by && ` by ${managerMessage.updated_by}`}
                    </div>
                )}

                <div className="flex justify-end">
                    <Button 
                        onClick={onSave}
                        disabled={savingMessage}
                        className={isResidential ? 'bg-stone-600 hover:bg-stone-700' : 'bg-emerald-600 hover:bg-emerald-700'}
                        data-testid="save-message-btn"
                    >
                        {savingMessage ? (
                            <>
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                                Saving...
                            </>
                        ) : (
                            <>
                                <Save className="w-4 h-4 mr-2" />
                                Save Message
                            </>
                        )}
                    </Button>
                </div>
            </div>

            {/* Preview */}
            {managerMessage.message && (
                <div className="space-y-2">
                    <Label className="text-base font-medium">Preview (as it will appear on homepage)</Label>
                    <div className={`card-base ${isResidential 
                        ? 'bg-gradient-to-r from-stone-50 to-amber-50 border-stone-200' 
                        : 'bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200'
                    }`}>
                        <div className="flex gap-3">
                            <MessageSquare className={`w-6 h-6 flex-shrink-0 mt-1 ${isResidential ? 'text-stone-600' : 'text-emerald-600'}`} />
                            <div>
                                <p className={`font-semibold mb-1 ${isResidential ? 'text-stone-800' : 'text-emerald-800'}`}>
                                    Message from Management
                                </p>
                                <p className={`whitespace-pre-wrap ${isResidential ? 'text-stone-700' : 'text-emerald-700'}`}>
                                    {managerMessage.message}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
